import BackButton from "@dashboard/components/BackButton";
import { ConfirmButton, ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { DashboardModal } from "@dashboard/components/Modal";
import { DialogProps } from "@dashboard/types";
import { Box, Text, Textarea } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { MAX_STAFF_NOTE_LENGTH } from "./useOrderSendEmail";

export interface OrderSendEmailDialogProps extends DialogProps {
  confirmButtonState: ConfirmButtonTransitionState;
  customerEmail: string;
  error: string | null;
  onSend: (note: string) => void;
}

/**
 * Sends the order's customer a message built from the store's own email
 * template, with an optional short note from staff.
 *
 * There is no subject field and no body editor: the SMTP app renders both from
 * the tenant's template. The recipient is shown but not editable — the app
 * derives it from the order, so it is displayed here to tell the operator who
 * is about to be mailed, not to let them choose.
 */
const OrderSendEmailDialog = ({
  open,
  onClose,
  onSend,
  confirmButtonState,
  customerEmail,
  error,
}: OrderSendEmailDialogProps) => {
  const intl = useIntl();
  const [note, setNote] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setNote("");
    }
  }, [open]);

  const isSending = confirmButtonState === "loading";

  return (
    <DashboardModal onChange={onClose} open={open}>
      <DashboardModal.Content size="sm">
        <DashboardModal.Header>
          {intl.formatMessage({
            id: "acNzUu",
            defaultMessage: "Send Email",
            description: "dialog header",
          })}
        </DashboardModal.Header>

        <Box display="grid" gap={4}>
          <Box>
            <Text size={2} color="default2" display="block">
              <FormattedMessage
                id="ais0ji"
                defaultMessage="To"
                description="email recipient field label"
              />
            </Text>
            <Text display="block" data-test-id="send-email-to">
              {customerEmail || (
                <FormattedMessage
                  id="RPed0B"
                  defaultMessage="This order has no customer email address."
                  description="send email dialog, missing recipient"
                />
              )}
            </Text>
          </Box>

          <Textarea
            label={intl.formatMessage({
              id: "cXS60C",
              defaultMessage: "Note to customer (optional)",
              description: "staff note field label",
            })}
            value={note}
            onChange={event => setNote(event.target.value)}
            maxLength={MAX_STAFF_NOTE_LENGTH}
            rows={6}
            disabled={isSending}
            data-test-id="send-email-note"
          />
          <Text size={2} color="default2" display="block">
            <FormattedMessage
              id="ChAjJu"
              defaultMessage="{numberOfCharacters} of {maxCharacters} characters"
              description="character limit"
              values={{
                maxCharacters: MAX_STAFF_NOTE_LENGTH,
                numberOfCharacters: note.length,
              }}
            />
          </Text>

          <Text size={2} color="default2" display="block">
            <FormattedMessage
              id="bVvvNf"
              defaultMessage="The message is built from your store's email template. Your note is added to it."
              description="send email dialog, template explanation"
            />
          </Text>

          {error && (
            <Text color="critical1" display="block" data-test-id="send-email-error">
              {error}
            </Text>
          )}
        </Box>

        <DashboardModal.Actions>
          <BackButton onClick={onClose} />
          <ConfirmButton
            transitionState={confirmButtonState}
            onClick={() => onSend(note)}
            disabled={!customerEmail || isSending}
            data-test-id="send-email-submit"
          >
            {intl.formatMessage({
              id: "aeZdA7",
              defaultMessage: "Send",
              description: "send email button",
            })}
          </ConfirmButton>
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

OrderSendEmailDialog.displayName = "OrderSendEmailDialog";
export default OrderSendEmailDialog;
